/**
 * AI'ın ürettiği rapor dosyaları.
 *
 * Üç yoldan geliyorlar:
 *   1. Sunucuda kalıcı saklanan çıktı (AiArtifactDto): akış sonunda `artifact` olayı ve
 *      kayıtlı sohbetin mesaj listesi. İçerik karttan açılınca çekilir.
 *   2. Canlı akışta `Write` aracı bir `.html` dosyası yazdığında (bkz. aiStream) — satır içi.
 *   3. Eski kayıtlar: sunucu raporu mesaj metnine ```html bloğu olarak katıyordu; blok
 *      metinden çıkarılıp kart yapılır (dizine yazılamayan Write çıktıları için hâlâ böyle).
 *
 * Ham HTML'i sohbet balonunda göstermek okunmaz bir duvar demek; blok metinden
 * çıkarılıp yerine kısa bir not bırakılıyor, rapor ise kart olarak açılıyor.
 */
import type { AiArtifactDto, ReportArtifact } from '@/shared/types/aiChat';

/** Mesaj metnindeki ```html bloklarını rapora çevirir. */
export function extractArtifacts(content: string): { cleaned: string; artifacts: ReportArtifact[] } {
  if (!content || !/```html/i.test(content)) return { cleaned: content, artifacts: [] };

  const artifacts: ReportArtifact[] = [];
  let index = 0;

  const cleaned = content.replace(/```html\s*([\s\S]*?)```/gi, (_match, code: string) => {
    const html = (code ?? '').trim();
    if (!html) return '';
    index += 1;
    const title = deriveTitle(html, index);
    artifacts.push({
      title,
      fileName: `${sanitizeFileName(title) || `rapor_${index}`}.html`,
      code: html,
      contentType: 'text/html',
      isText: true,
    });
    return `\n\n*📄 ${title}*\n`;
  });

  return { cleaned: cleaned.trim(), artifacts };
}

/** Sunucudaki çıktı üst bilgisi → kart modeli. */
export function artifactFromDto(dto: AiArtifactDto): ReportArtifact {
  return {
    artifactId: dto.artifactId,
    fileName: dto.fileName,
    title: dto.title || dto.fileName,
    contentType: dto.contentType,
    size: dto.size,
    isText: dto.isText,
    hasParameters: dto.hasParameters,
    hasQuery: dto.hasQuery,
    parameters: dto.parameters ?? [],
  };
}

export function isHtmlArtifact(a: { contentType?: string | null; fileName: string }): boolean {
  return /text\/html/i.test(a.contentType ?? '') || /\.html?$/i.test(a.fileName);
}

export function isPdfArtifact(a: { contentType?: string | null; fileName: string }): boolean {
  return /application\/pdf/i.test(a.contentType ?? '') || /\.pdf$/i.test(a.fileName);
}

export function isImageArtifact(a: { contentType?: string | null; fileName: string }): boolean {
  return /^image\//i.test(a.contentType ?? '') || /\.(png|jpe?g|svg)$/i.test(a.fileName);
}

/** Dosya adında uzantı yoksa verilen uzantıyı ekler. */
export function withExtension(fileName: string, ext: string | null): string {
  if (!ext || /\.[a-z0-9]{2,5}$/i.test(fileName)) return fileName;
  return `${fileName}.${ext}`;
}

/** Rapor başlığı: belgenin kendi başlığı, yoksa ilk H1, yoksa sıra numarası. */
export function deriveTitle(html: string, index = 1): string {
  const title = matchFirst(html, /<title[^>]*>([\s\S]*?)<\/title>/i);
  if (title) return stripTags(title);
  const h1 = matchFirst(html, /<h1[^>]*>([\s\S]*?)<\/h1>/i);
  if (h1) return stripTags(h1);
  return `Rapor ${index}`;
}

function matchFirst(input: string, regex: RegExp): string | null {
  const match = input.match(regex);
  return match && match[1] ? match[1].trim() : null;
}

function stripTags(input: string): string {
  return input
    .replace(/<[^>]+>/g, '')
    .trim()
    .slice(0, 80);
}

/** Dosya adı: Türkçe harfler korunur, ayraçlar alt çizgiye iner. */
export function sanitizeFileName(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9ğüşöçı\s-]/gi, '')
    .trim()
    .replace(/\s+/g, '_')
    .slice(0, 50);
}

/**
 * İçeriği indirilebilir dosyaya çevirir.
 *
 * Tarayıcıya bırakılan indirme, uygulamanın sekmesini terk etmeden çalışsın diye
 * blob + gizli bağlantı ile yapılıyor.
 */
export function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'rapor';
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Bazı tarayıcılar indirmeyi hemen başlatmıyor; URL biraz yaşasın.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Satır içi HTML raporu indirir. */
export function downloadHtml(artifact: ReportArtifact) {
  if (!artifact.code) return;
  downloadBlob(new Blob([artifact.code], { type: 'text/html;charset=utf-8' }), withExtension(artifact.fileName || 'rapor', 'html'));
}
